import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { User } from 'lucide-react'

interface ProfileSectionProps {
    user: any
    onSave?: () => void
}

export const ProfileSection: React.FC<ProfileSectionProps> = ({ user, onSave }) => {
    return (
        <div className="space-y-8">
            <Card className="border-border bg-card shadow-sm">
                <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <User className="size-4 opacity-70" />
                        Profile Information
                    </CardTitle>
                    <CardDescription>
                        Your personal details and account identity.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                    <div className="flex items-center gap-6">
                        <Avatar className="size-20 border-border border shadow-sm">
                            <AvatarImage src={user?.photoURL} />
                            <AvatarFallback className="bg-muted text-muted-foreground font-medium">
                                {user?.displayName?.charAt(0) || 'U'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="space-y-1.5">
                            <h4 className="font-semibold leading-none">{user?.displayName || 'User'}</h4>
                            <p className="text-sm text-muted-foreground">{user?.email}</p>
                        </div>
                    </div>

                    <Separator className="bg-border/60" />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="displayName" className="text-xs font-semibold text-muted-foreground">Display Name</Label>
                            <Input
                                id="displayName"
                                defaultValue={user?.displayName || ''}
                                className="bg-muted/30 border-border focus:ring-primary/20 transition-all font-medium py-1 h-9"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs font-semibold text-muted-foreground">Email Address</Label>
                            <div className="relative group flex-1">
                                <Input
                                    id="email"
                                    type="email"
                                    defaultValue={user?.email || ''}
                                    readOnly
                                    className="bg-muted font-medium py-1 h-9 border-border opacity-80 cursor-default text-muted-foreground w-full"
                                />
                            </div>
                        </div>
                    </div>
                    <Button 
                        onClick={onSave}
                        className="w-full sm:w-auto px-8 h-10 font-bold tracking-tight"
                    >
                        Save Profile Changes
                    </Button>
                </CardContent>
            </Card>

            <div className="flex justify-between items-center p-5 rounded-xl border border-destructive/20 bg-destructive/[0.02]">
                <div className="space-y-1">
                    <h4 className="font-semibold text-destructive text-sm flex items-center gap-2">
                        Danger Zone
                    </h4>
                    <p className="text-xs text-muted-foreground">Permanently remove your account and workspace data.</p>
                </div>
                <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive font-bold text-xs h-9">
                    Delete Account
                </Button>
            </div>
        </div>
    )
}
